import { Skeleton } from "@bednbody/ui/components/skeleton";
import { ReadFailedCard } from "@bednbody/ui/components/read-failed-card";
import { useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { useDispatchQueue } from "../command-center.hooks";

type QueueRows = NonNullable<ReturnType<typeof useDispatchQueue>["data"]>;

const SLICES = [
  { key: "available", label: "Available", color: "var(--success)" },
  { key: "assigned", label: "On trip", color: "var(--info)" },
  { key: "offline", label: "Offline", color: "var(--muted-foreground)" },
] as const;

/**
 * Rider fleet state donut from dispatch_queue (available | assigned | offline —
 * 0006). "Vehicles" are the riders' own motorcycles: there is no vehicles
 * table in the schema, so fleet state == rider state.
 */
export function FleetStatusDonut({
  queue,
  isLoading,
  failed = false,
}: {
  queue: QueueRows;
  isLoading: boolean;
  /** A failed read is not an empty fleet. */
  failed?: boolean;
}) {
  const data = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of queue) counts.set(row.status, (counts.get(row.status) ?? 0) + 1);
    return SLICES.map((s) => ({ ...s, value: counts.get(s.key) ?? 0 }));
  }, [queue]);

  const total = queue.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Skeleton className="h-32 w-32 border-8 border-border motion-reduce:animate-none" />
      </div>
    );
  }

  if (failed) {
    return (
      <ReadFailedCard message="The rider queue could not be read, so the fleet below is not the fleet." />
    );
  }

  if (total === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        No riders in the dispatch queue yet.
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <div className="relative h-36 w-36 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius="68%"
              outerRadius="95%"
              paddingAngle={2}
              strokeWidth={0}
            >
              {data.map((d) => (
                <Cell key={d.key} fill={d.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: "0.75rem",
                fontSize: "0.75rem",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tabular-nums text-foreground">{total}</span>
          <span className="type-body-micro text-muted-foreground">riders</span>
        </div>
      </div>

      <ul className="min-w-0 flex-1 space-y-2">
        {data.map((d) => (
          <li key={d.key} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 text-muted-foreground">
              <span className="size-2.5 rounded-full" style={{ background: d.color }} />
              {d.label}
            </span>
            <span className="font-medium tabular-nums text-foreground">
              {d.value}
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                {total > 0 ? `${Math.round((d.value / total) * 100)}%` : ""}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
